import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_335: DocMigration = {
  maxSchemaVersion: "0.0.335",
  description: "Move Room Floor Finish Material to Floor Finish Entity",
  apply(json: any): any {
    json.floorFinishes = {};
    if (json.rooms) {
      for (const roomId in json.rooms) {
        const room = json.rooms[roomId];
        const roomClass = room.attributes?.roomClass ?? defaultRoomAttributes[room.type].roomClass;
        const roomEnclosure = room.attributes?.roomEnclosure ?? defaultRoomAttributes[room.type].roomEnclosure;

        if (isInteriorRoomEnclosure[roomEnclosure] && room.floorMaterial) {
          const floorFinishId = uid();
          const floorFinish = {
            id: floorFinishId,
            parentId: roomId,
            name: "Interior Floor Finish",
            entityType: "FLOOR_FINISH",
            finishType: "INTERIOR",
            finishMaterial: room.floorMaterial,
            operationType: "ADD",
          };
          json.floorFinishes[floorFinishId] = floorFinish;
        } else if (
          roomEnclosure !== "NONE" ||
          (roomClass !== RoomClass.EXTERIOR && roomEnclosure === "NONE") ||
          (room.type === "WALK" && roomEnclosure === "NONE") ||
          (room.type === "PATIO" && roomEnclosure === "NONE" && roomClass === RoomClass.EXTERIOR)
        ) {
          const floorFinishId = uid();
          const floorFinish = {
            id: floorFinishId,
            parentId: roomId,
            name: "Migrated Floor Finish",
            entityType: "FLOOR_FINISH",
            finishType: "DYNAMIC",
            operationType: "ADD",
          };
          json.floorFinishes[floorFinishId] = floorFinish;
        }
        delete room.floorMaterial;
      }
    }

    return json;
  },
};

export default migration_0_0_335;

const isInteriorRoomEnclosure = {
  CONDITIONED: true,
  UNCONDITIONED: true,
};

enum RoomClass {
  HOUSE,
  BASEMENT,
  GARAGE,
  PORCH,
  DECK,
  PATIO,
  EXTERIOR,
}

const defaultRoomAttributes = {
  MASTER_BED: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  MASTER_BATH: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  MASTER_CLOSET: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  MASTER_HALL: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  MASTER_PASS: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  MASTER_SEAT: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  MASTER_SUITE: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  MASTER_VESTIBULE: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  SHOWER: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  WATER_CLOSET: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  BATH_HALL: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  MASTER_VANITY: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  BEDROOM: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  BED_PASS: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  REACH_CLOSET: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  BED_CLOSET: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  DINING: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  GUEST_SUITE: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  WALK_IN_CLOSET: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  POWDER: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  FORMAL_DINING: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  FORMAL_LIVING: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  KITCHEN: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  KITCHEN_CABINETS: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  DINING_NOOK: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  REFRIGERATOR: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  KITCHEN_HALL: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  PANTRY: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  LIVING: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  LIBRARY: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  SCREEN: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  LIVING_HALL: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  OFFICE: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  BAY_WINDOW: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  COAT_CLOSET: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  BATHFULL: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  BATHFULL_1_DOOR: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  BATHHALF: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  JJ_BATH: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  BED_BATH: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  BATHTUB: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  ENTRY: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  FOYER: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  ENTRY_BUFFER: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  OPEN_BELOW: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  GENERAL: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  GALLERY: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  GENERAL_OPEN: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  CLOSET: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  SOLARIUM: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  READING_NOOK: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  FLEX: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  CABINETS: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  HALL: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  HALL_NO_WINDOW: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  HALL_NO_ENTRY: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  STAIRS: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  STAIRS_EDITOR: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  CORNER_HALL: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  HALL_CASED_OPENING: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  GARAGE: {
    roomEnclosure: "UNCONDITIONED",
    roomClass: RoomClass.GARAGE,
  },
  GARAGE_DETACH: {
    roomEnclosure: "UNCONDITIONED",
    roomClass: RoomClass.GARAGE,
  },
  UTIL_HALL: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  MECH: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  MUDROOM: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  DOG: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  JUMP: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  LAUNDRY: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  WORKSHOP: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  MUD_LAUNDRY: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  FRONT_PORCH: {
    roomEnclosure: "OPEN",
    roomClass: RoomClass.PORCH,
  },
  REAR_PORCH: {
    roomEnclosure: "OPEN",
    roomClass: RoomClass.PORCH,
  },
  PATIO: {
    roomEnclosure: "NONE",
    exteriorFloorMaterial: "walks0",
    roomClass: RoomClass.PATIO,
  },
  WALK: {
    roomEnclosure: "NONE",
    roomClass: RoomClass.EXTERIOR,
  },
  BREEZEWAY: {
    roomEnclosure: "OPEN",
    exteriorFloorMaterial: "walks0",
    roomClass: RoomClass.EXTERIOR,
  },
  FIREPLACE: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  FREESTANDING_FIREPLACE: {
    roomEnclosure: "OPEN",
    roomClass: RoomClass.EXTERIOR,
  },
  PORTICO: {
    roomEnclosure: "EXTERIOR",
    roomClass: RoomClass.PORCH,
  },
  NONE: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  CHASE: {
    roomEnclosure: "CONDITIONED",
    roomClass: RoomClass.HOUSE,
  },
  LANDSCAPE: {
    roomEnclosure: "NONE",
    roomClass: RoomClass.EXTERIOR,
  },
};
